import axios, { CancelToken } from 'axios';
import qs from 'qs';
import { ClientDto, ClientWithUser } from '@/features/client-admins/clients.dto';
import { DeviceModel } from '@/features/models/models.dto';
import {
	ClientsUsersByCompanyParams,
	ClientUsersByCompanyFilterType,
	CompanyCountriesDto,
	CompanyDto,
	CompanyResponseDto,
	CreateCompanyWithUserDto,
	DeleteCompaniesResponseDto,
	ProviderCompanyDto,
} from '@/features/service-providers/companies.dto';
import { ClientAdminUserDto, UserDto } from '@/features/users/users.dto';
import { Countries } from '@/OptimusRoutes/entities/countries.entity';
import { Roles } from '@/OptimusRoutes/entities/roles.entity';
import { ApiResult, Filter, ServiceResponse } from '@/OptimusRoutes/interfaces';
import { DeviceModelDto } from '../modelProfile/modelProfile.dto';

interface ICompany {
	getAllCompaniesWithProviders: () => Promise<ServiceResponse<ApiResult<ProviderCompanyDto[]>>>;
	update: (id: number, payload: Partial<CompanyDto>, userId: number) => Promise<ServiceResponse<CompanyDto>>;
	getAllCompanies: () => Promise<ServiceResponse<CompanyResponseDto[]>>;
	createCompanyWithUser: (payload: CreateCompanyWithUserDto) => Promise<ServiceResponse<ProviderCompanyDto>>;
	$delete: (ids: number[]) => Promise<ServiceResponse<DeleteCompaniesResponseDto>>;
	updateClient: (id: number, clientId: number, payload: Partial<ClientDto>, userId: number) => Promise<ClientDto>;
	createClient: (id: number, payload: Partial<ClientDto>, user: ClientAdminUserDto) => Promise<ClientDto>;
	getAllClientAdmins: (id: number, clientId: number) => Promise<ServiceResponse<UserDto[]>>;
	getClientUsers: (params: ClientsUsersByCompanyParams) => Promise<ServiceResponse<UserDto[]>>;
	getAllClients: (id: number, includeOwn: boolean) => Promise<ServiceResponse<ApiResult<ClientDto[]>>>;
	getCompanyModelProfiles: (id: number) => Promise<DeviceModel[]>;
	deleteClientById: (companyId: number, clientId: number) => Promise<ServiceResponse>;
	getClientsWithMainUser: (
		id: number,
		filter: Filter<ClientDto>
	) => Promise<ServiceResponse<ApiResult<ClientWithUser[]>>>;
	getCompanyModels: (
		id: number,
		filter: Filter<DeviceModelDto>,
		cancelToken?: CancelToken
	) => Promise<ServiceResponse<ApiResult<DeviceModelDto[]>>>;
	getCountries: () => Promise<ServiceResponse<Countries[]>>;
	getCompanyRoles: (companyId: number) => Promise<ServiceResponse<Roles[]>>;
	getCompanyClients: (
		id: number,
		filter: Filter<ClientDto>,
		includeOwn: boolean,
		cancelToken?: CancelToken
	) => Promise<ServiceResponse<ApiResult<ClientDto[]>>>;
	editCountries: (companyId: number, countries: number[]) => Promise<ServiceResponse<CompanyCountriesDto[]>>;
	getCountriesByCompanyId: (id: number) => Promise<ApiResult<Countries[]>>;
}

class Service implements ICompany {
	async update(id: number, payload: Partial<CompanyDto>, userId: number): Promise<ServiceResponse<CompanyDto>> {
		const { data } = await axios.patch<ServiceResponse<CompanyDto>>(`/v2/companies/${id}`, { payload, userId });
		return data;
	}

	getAllCompanies = async (): Promise<ServiceResponse<CompanyResponseDto[]>> => {
		const { data } = await axios.get<ServiceResponse<CompanyResponseDto[]>>('/v2/companies/all');
		return data;
	};
	getAllCompaniesWithProviders = async (): Promise<ServiceResponse<ApiResult<ProviderCompanyDto[]>>> => {
		const { data } = await axios.get<ServiceResponse<ApiResult<ProviderCompanyDto[]>>>(
			'/v2/companies/service-providers'
		);
		return data;
	};

	getCompanyModels = async (
		companyId: number,
		{ skip, take, filter = {} }: Filter<DeviceModelDto>,
		cancelToken?: CancelToken
	): Promise<ServiceResponse<ApiResult<DeviceModelDto[]>>> => {
		const params = { skip, take, filter: JSON.stringify(filter) };
		const { data } = await axios.get<ServiceResponse<ApiResult<DeviceModelDto[]>>>(
			`/v2/companies/${companyId}/models?${qs.stringify(params)}`,
			{ cancelToken }
		);
		return data;
	};
	updateClient = async (
		id: number,
		clientId: number,
		payload: Partial<ClientDto>,
		userId: number
	): Promise<ClientDto> => {
		const { data } = await axios.patch<ClientDto>(`/v2/companies/${id}/clients/${clientId}`, { payload, userId });
		return data;
	};

	getClientUsers = async ({
		id,
		clientId,
		search,
		filterType,
	}: ClientsUsersByCompanyParams): Promise<ServiceResponse<UserDto[]>> => {
		const params = { search, filterType };
		const { data } = await axios.get<ServiceResponse<UserDto[]>>(
			`/v2/companies/${id}/clients/${clientId}/users?${qs.stringify(params)}`
		);
		return data;
	};
	async createCompanyWithUser(payload: CreateCompanyWithUserDto): Promise<ServiceResponse<ProviderCompanyDto>> {
		const { data } = await axios.post<ServiceResponse<ProviderCompanyDto>>(
			'/v2/companies/create-with-user',
			payload
		);
		return data;
	}
	$delete = async (ids: number[]): Promise<ServiceResponse<DeleteCompaniesResponseDto>> => {
		const { data } = await axios.delete<ServiceResponse<DeleteCompaniesResponseDto>>('/v2/companies', {
			data: { ids },
		});
		return data;
	};
	getClientsWithMainUser = async (
		id: number,
		{ take = 10, skip = 1, search }: Filter<ClientDto>
	): Promise<ServiceResponse<ApiResult<ClientWithUser[]>>> => {
		const params = { search, take, skip: (skip - 1) * take };
		const { data } = await axios.get<ServiceResponse<ApiResult<ClientWithUser[]>>>(
			`/v2/companies/${id}/clients-with-users?${qs.stringify(params)}`
		);
		return data;
	};

	getAllClientAdmins = async (id: number, clientId: number): Promise<ServiceResponse<UserDto[]>> =>
		await this.getClientUsers({ id, clientId, filterType: ClientUsersByCompanyFilterType.CLIENT_ADMIN_USERS });

	getAllClients = async (
		id: number,
		includeOwn: boolean = false
	): Promise<ServiceResponse<ApiResult<ClientDto[]>>> => {
		const filter = {
			order: {
				name: 'ASC',
			},
		};
		const params = { skip: 0, take: 0, filter: JSON.stringify(filter), includeOwn };
		const { data } = await axios.get<ServiceResponse<ApiResult<ClientDto[]>>>(
			`/v2/companies/${id}/clients?${qs.stringify(params)}`
		);
		return data;
	};
	getCompanyClients = async (
		id: number,
		{ skip, take, filter = {}, search }: Filter<ClientDto>,
		includeOwn: boolean = false,
		cancelToken?: CancelToken
	): Promise<ServiceResponse<ApiResult<ClientDto[]>>> => {
		if (search) {
			filter.where = {
				...filter.where,
				name: {
					op: 'like',
					value: search,
				},
			};
		}
		const params = { skip, take, filter: JSON.stringify(filter), includeOwn };
		const { data } = await axios.get<ServiceResponse<ApiResult<ClientDto[]>>>(
			`/v2/companies/${id}/clients?${qs.stringify(params)}`,
			{ cancelToken }
		);
		return data;
	};

	async createClient(id: number, client: Partial<ClientDto>, user: ClientAdminUserDto): Promise<ClientDto> {
		const { data } = await axios.post<ClientDto>(`/v2/companies/${id}/clients`, { client, user });
		return data;
	}

	async getCompanyModelProfiles(id: number): Promise<DeviceModel[]> {
		const { data } = await axios.get<DeviceModel[]>(`/v2/companies/${id}/device-models`);
		return data;
	}

	async deleteClientById(companyId: number, clientId: number): Promise<ServiceResponse> {
		const { data } = await axios.delete<ServiceResponse>(`/v2/companies/${companyId}/clients/${clientId}`);
		return data;
	}

	async getCountries(): Promise<ServiceResponse<Countries[]>> {
		const { data } = await axios.get<ServiceResponse<Countries[]>>('/v2/companies/countries');
		return data;
	}

	async getCompanyRoles(companyId: number): Promise<ServiceResponse<Roles[]>> {
		const { data } = await axios.get(`/v2/companies/${companyId}/roles`);
		return data;
	}

	async editCountries(companyId: number, countries: number[]): Promise<ServiceResponse<CompanyCountriesDto[]>> {
		const { data } = await axios.post<ServiceResponse<CompanyCountriesDto[]>>(
			`/v2/companies/${companyId}/company-countries`,
			{ countries }
		);
		return data;
	}

	async getCountriesByCompanyId(id: number): Promise<ApiResult<Countries[]>> {
		const { data } = await axios.get<ApiResult<Countries[]>>(`/v2/companies/${id}/countries`);
		return data;
	}
}

const CompanyService = new Service();

export default CompanyService;
